import { useCallback, useEffect, useState } from 'react';
import ShelfFilters, { useShelfFilter } from '../ShelfFilters';
import { ErrorBanner } from '../Feedback';
import { api } from '../../api/client';

/**
 * "Who has taken this book?" — one title, a page of schools.
 *
 * <p>The other half of what a School × Title grid was trying to be, and the half it
 * was worst at: finding one title's column among 285 and reading down it. One column
 * and a paged list answers the same question at any catalogue size, and it can carry
 * the city, the rep and the date, which a cell never could.
 *
 * <p>The title is chosen with the same subject and class chips the Content Library
 * and the student's shelf use — a title *is* a subject and a class, so the control
 * that narrows books by those is already written.
 */
export default function TitleAdoptions({ titles }) {
  const [titleId, setTitleId] = useState(null);
  const [data, setData] = useState(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState(null);
  const { shelf, filterProps } = useShelfFilter(titles);

  const load = useCallback(
    async (pageNumber = 0) => {
      if (!titleId) {
        setData(null);
        return;
      }
      setLoading(true);
      try {
        setData(await api.titleAdoptions(titleId, { query, page: pageNumber, size: 20 }));
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [titleId, query],
  );

  useEffect(() => {
    // Debounced, like every other search in the back office: a keystroke per request
    // against a school list is a request per keystroke.
    const handle = setTimeout(() => load(0), 250);
    return () => clearTimeout(handle);
  }, [load]);

  async function toggle(row) {
    setSavingId(row.schoolId);
    setError(null);
    try {
      const updated = await api.toggleAdoption({
        schoolId: row.schoolId,
        titleId,
        adopted: !row.adopted,
      });
      // One row patched rather than the page refetched: the rest of it cannot have
      // changed, and a reload would lose the scroll position mid-list.
      setData((current) => ({
        ...current,
        adoptedCount: current.adoptedCount + (updated.adopted ? 1 : -1),
        schools: {
          ...current.schools,
          items: current.schools.items.map((candidate) =>
            candidate.schoolId === row.schoolId
              ? { ...candidate, adopted: updated.adopted, adoptedDate: updated.adoptedDate }
              : candidate,
          ),
        },
      }));
    } catch (err) {
      setError(err);
    } finally {
      setSavingId(null);
    }
  }

  const chosen = titles.find((title) => title.id === titleId);

  return (
    <>
      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      <section className="card card-pad" style={{ marginBottom: 'var(--s1)' }}>
        <ShelfFilters {...filterProps} />

        <p className="small muted" style={{ margin: '0 0 var(--s0)' }}>
          {shelf.matches.length === 0
            ? 'No title matches this filter.'
            : `Pick a book — ${shelf.matches.length} to choose from`}
        </p>
        <div className="filter-chips">
          {shelf.matches.map((title) => (
            <button
              type="button"
              key={title.id}
              className="chip"
              aria-pressed={title.id === titleId}
              onClick={() => setTitleId(title.id === titleId ? null : title.id)}
            >
              {title.name}
            </button>
          ))}
        </div>
      </section>

      {!titleId && (
        <div className="card empty">Choose a book above to see which schools have taken it.</div>
      )}

      {titleId && (
        <section className="card">
          <div
            className="toolbar"
            style={{ padding: 'var(--s1) var(--s2)', justifyContent: 'space-between' }}
          >
            <span>
              <b>{chosen?.name}</b>
              <span className="muted small"> · {chosen?.code}</span>
            </span>
            <span className="muted small">
              {data ? `taken by ${data.adoptedCount} of ${data.schools.totalItems} schools` : '—'}
            </span>
          </div>

          <div className="toolbar" style={{ padding: '0 var(--s2) var(--s1)' }}>
            <input
              className="input"
              style={{ flex: '1 1 220px' }}
              placeholder="Search school name, code or city"
              aria-label="Search schools"
              maxLength={100}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>

          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 56 }}>Taken</th>
                  <th>School</th>
                  <th>City</th>
                  <th>Sales rep</th>
                  <th>Since</th>
                </tr>
              </thead>
              <tbody>
                {(data?.schools.items ?? []).map((row) => (
                  <tr key={row.schoolId}>
                    <td>
                      <label className="adopt">
                        <input
                          type="checkbox"
                          checked={row.adopted}
                          disabled={!row.editable || savingId === row.schoolId}
                          aria-label={`${row.schoolName} adopts ${chosen?.name ?? 'this title'}`}
                          onChange={() => toggle(row)}
                        />
                        <span className="adopt-box" aria-hidden="true" />
                      </label>
                    </td>
                    <td>
                      {row.schoolName}
                      <span className="muted small"> · {row.schoolCode}</span>
                    </td>
                    <td>{row.city}</td>
                    <td>{row.salesRepName ?? <span className="muted">Unassigned</span>}</td>
                    <td>
                      {row.adoptedDate ? (
                        new Date(row.adoptedDate).toLocaleDateString(undefined, { dateStyle: 'medium' })
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {loading && <div className="empty">Loading…</div>}
          {!loading && (data?.schools.items ?? []).length === 0 && (
            <div className="empty">No school matches.</div>
          )}

          <div
            className="toolbar"
            style={{ padding: 'var(--s1) var(--s2)', justifyContent: 'space-between' }}
          >
            <span className="muted small">{data?.schools.totalItems ?? 0} school(s)</span>
            <span style={{ display: 'flex', gap: 'var(--s0)' }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={!data || data.schools.page === 0}
                onClick={() => load(data.schools.page - 1)}
              >
                Previous
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={!data?.schools.hasNext}
                onClick={() => load(data.schools.page + 1)}
              >
                Next
              </button>
            </span>
          </div>
        </section>
      )}
    </>
  );
}
